// Multi-territory API utilities
// TypeScript equivalent of src/api/apiUtils.js for MT services

import { MT_BASE_URL } from '@/multi-territory/mt-services/mt-api-endpoints';
import { getAccessToken } from '@/utils/session';

export type MTApiResponse<T = unknown> =
  | { success: true; data: T }
  | { success: false; error: string; status?: number }

/**
 * Parse JSON response and handle the nested API structure.
 * API responses follow: { data: <payload>, success: true }
 * Or on validation error: { errors: [...], success: false }
 */
export const mtParseJsonResponse = async <T = unknown>(
  response: Response
): Promise<MTApiResponse<T>> => {
  const json = await response.json()

  // Check for validation errors at top level: { errors: [...], success: false }
  if (json && json.success === false && json.errors) {
    const errorMsg = json.errors
      .map((e: any) => (typeof e === 'string' ? e : e.message))
      .filter(Boolean)
      .join(', ')
    return { success: false, error: errorMsg }
  }

  // Unwrap nested data: { data: <payload>, success: true }
  const innerData = json?.data

  // Check for validation errors nested in data: { data: { errors: [...], success: false } }
  if (
    innerData &&
    typeof innerData === 'object' &&
    innerData.success === false &&
    innerData.errors
  ) {
    const errorMsg = innerData.errors
      .map((e: any) => (typeof e === 'string' ? e : e.message))
      .filter(Boolean)
      .join(', ')
    return { success: false, error: errorMsg }
  }

  // Return unwrapped data (or json itself if no data key)
  return { success: true, data: (innerData ?? json) as T }
}

/**
 * Parse JSON response expecting a string message (save/update/delete).
 * Extracts the message string from the nested API structure.
 */
export const mtParseMessageResponse = async (
  response: Response
): Promise<MTApiResponse<string>> => {
  const json = await response.json()

  // Check for validation errors at top level
  if (json && json.success === false && json.errors) {
    const errorMsg = json.errors
      .map((e: any) => (typeof e === 'string' ? e : e.message))
      .filter(Boolean)
      .join(', ')
    return { success: false, error: errorMsg }
  }

  const innerData = json?.data

  // Check for validation errors nested in data
  if (
    innerData &&
    typeof innerData === 'object' &&
    innerData.success === false &&
    innerData.errors
  ) {
    const errorMsg = innerData.errors
      .map((e: any) => (typeof e === 'string' ? e : e.message))
      .filter(Boolean)
      .join(', ')
    return { success: false, error: errorMsg }
  }

  // Extract message string
  const message: string =
    typeof innerData === 'string' ? innerData : (innerData ?? '')
  return { success: true, data: message }
}

export const mtHandleSuccessResponse = async <T = unknown>(
  response: Response
): Promise<MTApiResponse<T>> => {
  const contentType = response.headers.get('content-type')
  if (contentType?.includes('application/json')) {
    const data = (await response.json()) as T
    return { success: true, data }
  } else {
    const blob = await response.blob()
    return { success: true, data: blob as unknown as T }
  }
}

export const mtHandleErrorResponse = (error: unknown): MTApiResponse<never> => {
  const message = error instanceof Error ? error.message : 'Unknown Error'
  return { success: false, error: message }
}

export const mtHandleRequestFailure = async (
  response: Response
): Promise<MTApiResponse<never>> => {
  let errorMessage: string
  try {
    const errorData = await response.json()
    if (errorData.errors && Array.isArray(errorData.errors)) {
      errorMessage = errorData.errors
        .map((e: any) => (typeof e === 'string' ? e : e.message))
        .filter(Boolean)
        .join(', ')
    } else {
      errorMessage =
        errorData.error ||
        errorData.message ||
        `Request failed with status: ${response.status}`
    }
  } catch {
    errorMessage = `Request failed with status: ${response.status}`
  }
  return { success: false, error: errorMessage, status: response.status }
}

export const mtBuildRequestHeader = (
  method: string = 'GET',
  payload: unknown = null,
  extraHeaders: Record<string, string> = {}
): RequestInit => {
  const token = getAccessToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: token ?? '',
    ...extraHeaders,
  }
  return {
    method,
    headers,
    body: payload ? JSON.stringify(payload) : undefined,
  }
}

export const mtUrlBuilder = (
  endpoint: string,
  pathParams: Record<string, string | number | undefined | null> = {},
  searchParams: Record<string, string | number | undefined | null> = {}
): string => {
  const baseUrl = MT_BASE_URL ?? ''
  let path = `${baseUrl}/${endpoint}`.replace(/\/+$/, '')

  Object.values(pathParams).forEach(value => {
    if (value !== undefined && value !== null) {
      path += `/${encodeURIComponent(String(value))}`
    }
  })

  const url = new URL(path, window.location.origin)

  Object.entries(searchParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, String(value))
    }
  })

  return url.toString()
}
